// Define a global variable for the timeout to avoid TypeScript errors
if (typeof window !== 'undefined') {
    window._boldFormatterTimeout = null;
}

/**
 * Format text with ** markers as bold in the entire DOM
 */
export const formatBoldTextInDOM = (boldClass = 'font-bold') => {
    // console.log('Starting DOM-based bold text formatting');

    // Pattern to match text between ** markers
    const pattern = /\*\*(.*?)\*\*/g;

    // Helper function to process text nodes
    function processTextNode(node) {
        const text = node.textContent;

        // Skip if no ** markers found
        if (!text || !text.includes('**')) {
            return false;
        }

        // Create a fragment to hold the processed content
        const fragment = document.createDocumentFragment();

        // Split by ** markers
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(text)) !== null) {
            // Add text before the match
            const beforeText = text.substring(lastIndex, match.index);
            if (beforeText) {
                fragment.appendChild(document.createTextNode(beforeText));
            }

            // Add the bold text
            const boldText = match[1]; // Get the text between ** markers
            const boldSpan = document.createElement('span');
            boldSpan.className = boldClass;
            boldSpan.textContent = boldText;
            fragment.appendChild(boldSpan);

            // Update last index
            lastIndex = pattern.lastIndex;
        }

        // Add any remaining text after the last match
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }

        // Replace the original text node with our processed fragment
        node.parentNode.replaceChild(fragment, node);
        return true;
    }

    // Helper function to process an element and its children
    function walkDOM(element) {
        // Skip certain elements
        const skipTags = ['SCRIPT', 'STYLE', 'SVG', 'CANVAS'];
        if (skipTags.includes(element.tagName)) {
            return;
        }

        // Process child text nodes
        const childNodes = Array.from(element.childNodes);

        for (const node of childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                processTextNode(node);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                walkDOM(node);
            }
        }
    }

    // Process the entire document body
    if (document.body) {
        walkDOM(document.body);
    }

    // console.log('Completed DOM-based bold text formatting');

    // Return the walkDOM function for reuse with the observer
    return walkDOM;
};

/**
 * Initialize the bold text formatter
 */
export const initBoldTextFormatting = (boldClass = 'font-bold') => {
    // Skip if not in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    // console.log('Initializing bold text formatter');

    // Store the walkDOM function for later use by the observer
    let walkDOM;

    // Format once when the page loads
    const initialFormat = () => {
        walkDOM = formatBoldTextInDOM(boldClass);
    };

    if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', () => {
            setTimeout(initialFormat, 100);
        });
    } else {
        setTimeout(initialFormat, 100);
    }

    // Setup a mutation observer to handle dynamic content
    setTimeout(() => {
        const observer = new MutationObserver((mutations) => {
            // Debounce multiple rapid changes
            if (window._boldFormatterTimeout) {
                clearTimeout(window._boldFormatterTimeout);
            }

            window._boldFormatterTimeout = setTimeout(() => {
                let hasNewNodes = false;

                for (const mutation of mutations) {
                    if (mutation.addedNodes.length > 0) {
                        hasNewNodes = true;
                        break;
                    }
                }

                if (hasNewNodes && walkDOM) {
                    // console.log('New content detected, formatting bold text');
                    formatBoldTextInDOM(boldClass);
                }
            }, 200);
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // console.log('Bold text observer started');
    }, 500);
};